import { Card, DatePicker } from "antd";
import React, { useEffect, useState } from "react";
import ReactApexChart from "react-apexcharts";
import { GetDashboardSLAData } from "../../../../utils/api.constant";
import { eResultCode } from "../../../../utils/enum";
import useFetch from "../../../../hooks/useFetch";
import { ApexOptions } from "apexcharts";
import dayjs, { Dayjs } from "dayjs";
import DateUtils from "../../../../utils/date";
import { TDropdownOptions } from "../../../../types/config";
import { monthOnlyFormat } from "../../../../utils/constants";

type SlaGaugeProps = {
  viewDate: string;
  customerIds: string[];
  servicePartnerIds: string[];
  customerOptions: TDropdownOptions[];
  partnerOptions: TDropdownOptions[];
  onDataFetch: (data: any) => void;
};

const SlaGauge: React.FC<SlaGaugeProps> = ({
  viewDate,
  customerIds,
  servicePartnerIds,
  customerOptions,
  partnerOptions,
  onDataFetch,
}) => {
  const { post } = useFetch();
  const [selectedMonth, setSelectedMonth] = useState<Dayjs>(
    dayjs().startOf("month")
  );
  const [serviceSla, setServiceSla] = useState<number[]>([0]);
  const [installationSla, setInstallationSla] = useState<number[]>([0]);

  const gaugeOptions: ApexOptions = {
    // Gauge options remain unchanged
    chart: {
      type: "donut",

      offsetY: -20,
      sparkline: {
        enabled: true,
      },
    },
    plotOptions: {
      radialBar: {
        startAngle: -90,
        endAngle: 90,
        track: {
          background: "#e7e7e7",
          strokeWidth: "97%",
          margin: 5,
        },
        dataLabels: {
          name: {
            show: false,
          },
          value: {
            offsetY: -2,
            fontSize: "22px",
          },
        },
      },
    },
    grid: {
      padding: {
        top: -10,
      },
    },
    fill: {
      type: "gradient",
      gradient: {
        shade: "light",
        shadeIntensity: 0.4,
        inverseColors: false,
        opacityFrom: 1,
        opacityTo: 1,
        colorStops: [
          {
            offset: 0,
            color: "#34C759", // Red
            opacity: 1,
          },
          {
            offset: 50,
            color: "#FFCC00", // Yellow/Orange
            opacity: 1,
          },
          {
            offset: 100,
            color: "#FF3B30", // Green
            opacity: 1,
          },
        ],
      },
    },
    labels: ["Average Results"],
  };

  const handleMonthChange = (
    month: Dayjs | null,
    monthString: string | string[]
  ) => {
    if (month) {
      // const formattedMonth = month.format("YYYY-MM"); // Format the selected month as YYYY-MM
      setSelectedMonth(month); // Update the selected month in state
    } else {
      console.log("No month selected");
    }
  };

  const getSlaData = async () => {
    if (customerIds?.length && servicePartnerIds.length) {
      try {
        const payload = {
          data: {
            viewDate: DateUtils.getDateStringWithOutTimezone(
              new Date(viewDate).toDateString()
            ),
            IsAllCustomerRequest: customerIds?.includes("ALL") ? 1 : 0,
            IsAllServicePartnerRequest: servicePartnerIds?.includes("ALL")
              ? 1
              : 0,
            customerId: customerIds?.includes("ALL")
              ? customerOptions
                  ?.filter((option) => option.value !== "ALL")
                  .map((option) => option.value)
              : customerIds,
            servicePartnerId: servicePartnerIds?.includes("ALL")
              ? partnerOptions
                  ?.filter((option) => option.value !== "ALL")
                  .map((option) => option.value)
              : servicePartnerIds,
          },
        };

        const response = await post(GetDashboardSLAData, payload);
        if (response) {
          const { data, dataResponse } = response;
          const { returnCode } = dataResponse;
          if (returnCode === eResultCode.SUCCESS) {
            console.log("kkkkkkk", data);

            onDataFetch(data);
            setServiceSla([Number(data.serviceSLA)]);
            setInstallationSla([Number(data.installationSLA)]);
          }
        }
      } catch (error) {
        console.error("Error fetching issue category data:", error);
      }
    }
  };

  // Helper function to disable future months
  const disableFutureMonths = (current: Dayjs | null): boolean => {
    // Ensure the function always returns a boolean
    return current ? current.isAfter(dayjs().endOf("month")) : false;
  };

  // Helper function to format SLA values
  const formatSla = (sla: number[]) =>
    sla.map((value) => parseFloat(value.toFixed(2)));

  // Formatted SLA arrays for the charts
  const formattedServiceSla = formatSla(serviceSla);
  const formattedInstallationSla = formatSla(installationSla);

  useEffect(() => {
    if (customerIds?.length && servicePartnerIds?.length) {
      getSlaData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewDate, JSON.stringify(customerIds), JSON.stringify(servicePartnerIds), selectedMonth]);

  return (
    <Card bordered={false} style={{ borderRadius: "1px" }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          marginTop: "5px",
        }}
      >
        <strong>SLA</strong>
        <div style={{ width: "20%" }}>
          <span>{DateUtils.format(viewDate, monthOnlyFormat)}</span>
        </div>
      </div>
      <div>
        <div>
          <ReactApexChart
            options={gaugeOptions}
            series={formattedServiceSla}
            type="radialBar"
            height={278}
          />
          <div style={{ textAlign: "center", margin: "0 5px" }}>
            Service = {formattedServiceSla[0].toFixed(2)}%
          </div>
        </div>
        <div>
          <ReactApexChart
            options={gaugeOptions}
            series={formattedInstallationSla}
            type="radialBar"
            height={278}
          />
          <div style={{ textAlign: "center" }}>
            Installation = {formattedInstallationSla[0].toFixed(2)}%
          </div>
        </div>
      </div>
    </Card>
  );
};

export default SlaGauge;
